import { RestError } from '@azure/core-http';
import { CourierDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { castDraft } from 'immer';
import { useImmer } from 'use-immer';
import { naturalSort } from '../../../../../core/util/natural-sort.util';
import { DropdownOption } from '../../../../../ui/components/pure/dropdown/dropdown.component';
import { useQueryGetCarrierHierarchy } from '../../../../carriers/carriers.queries';
import { useMutationMoveCourier } from '../../../couriers.queries';
import { MoveCourierFormState, makeMoveCourierFormConfig, moveDestinationUnits } from '../../../logic/courier-form.map';

export type MoveCourierActionState = {
  saving: boolean;
  error?: RestError | Error;
};

export interface MoveCourierActionProps {
  courier: CourierDto;
  /** The project the page was reached through — the starting point for finding the destinations. */
  projectID: string;
  onAbort(): void;
  onSuccess(): void;
}

export function useMoveCourierAction({ courier, projectID, onAbort, onSuccess }: MoveCourierActionProps) {
  const [state, setState] = useImmer<MoveCourierActionState>({ saving: false });

  const { control, handleSubmit } = useForm<MoveCourierFormState>();

  // The courier's organisation (their company, or the network for a pool's carrier) is the boundary
  // the move endpoint enforces, so its hierarchy lists every unit a move would be accepted for.
  const organisationID = courier.organisation?.id;
  const fromOrganisation = useQueryGetCarrierHierarchy(organisationID);

  // A region's admins cannot read the company above them. The project the page was reached through
  // then answers with the only units they are entitled to.
  const fromPage = useQueryGetCarrierHierarchy(projectID, {
    enabled: !organisationID || fromOrganisation.isError
  });

  const hierarchy = fromOrganisation.data ?? fromPage.data;

  const units = useMemo(() => moveDestinationUnits(hierarchy), [hierarchy]);

  // The current unit stays listed so a half-typed name still matches; the config rejects picking it.
  const unitMenuItems: DropdownOption<string>[] = useMemo(
    () => naturalSort(units.map((unit) => ({ label: unit.name, value: unit.id }))).asc((option) => option.label),
    [units]
  );

  const formConfig = makeMoveCourierFormConfig(courier.carrier?.id);

  // Falls back to the courier's own record for a unit the caller cannot see.
  const currentUnitLabel =
    units.find((unit) => unit.id === courier.carrier?.id)?.name ?? courier.carrier?.name ?? 'Unknown';

  const moveCourierMutation = useMutationMoveCourier(courier.id);

  const handlers = {
    onCancel: onAbort,
    onSubmit(formValues: MoveCourierFormState) {
      // Only reachable if a submit slips past the field's validation.
      if (!formValues.groupID || formValues.groupID === courier.carrier?.id) {
        onAbort();
        return;
      }

      setState((draft) => {
        draft.saving = true;
      });

      moveCourierMutation.mutate(
        { groupID: formValues.groupID },
        {
          onSuccess() {
            onSuccess();
          },
          onError(error) {
            setState((draft) => {
              draft.saving = false;
              draft.error = castDraft(error);
            });
          }
        }
      );
    }
  };

  return {
    state,
    handlers,
    control,
    handleSubmit,
    formConfig,
    unitMenuItems,
    currentUnitLabel,
    loading: fromOrganisation.isLoading || fromPage.isLoading
  } as const;
}
